<template>
  <div>
    <div 
      ref="backgroundRef"
      class="background-layer" 
      :class="{ error: isBackgroundError }"
      :style="backgroundStyle"
    ></div>
    <div class="overlay" v-if="!isBackgroundError"></div>
  </div>
</template>

<script>
import { computed, ref, onMounted, watch, nextTick } from 'vue'

export default {
  name: 'BackgroundLayer',
  props: {
    backgroundUrl: {
      type: String,
      required: true
    },
    isBackgroundError: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const isImageLoaded = ref(false)
    const backgroundRef = ref(null)
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
    
    const backgroundStyle = computed(() => {
      if (props.isBackgroundError) {
        return {
          backgroundImage: 'none'
        }
      }
      
      if (!isImageLoaded.value) {
        return {
          backgroundColor: '#000000',
          backgroundImage: 'none'
        }
      }
      
      return {
        backgroundImage: `url('${props.backgroundUrl}')`
      }
    })
    
    const forceRepaint = () => {
      if (isIOS && backgroundRef.value) {
        nextTick(() => {
          const el = backgroundRef.value
          
          // iOS Safari 强制重绘：多种策略组合
          // 1. 强制重排
          el.style.transform = 'translateZ(0)'
          el.offsetHeight
          
          // 2. 触发重绘
          el.style.opacity = '0.99'
          el.offsetWidth
          el.style.opacity = '1'
          
          // 3. 最终恢复
          setTimeout(() => {
            el.style.transform = 'scale(1.05)'
          }, 50)
        })
      }
    }
    
    // iOS PWA模式下彻底设置背景图片，确保完全覆盖屏幕
    const setBodyBackground = (url) => {
      if (isIOS && window.matchMedia('(display-mode: standalone)').matches) {
        const htmlElement = document.documentElement
        const bodyElement = document.body
        
        if (url && !props.isBackgroundError) {
          // 创建一个绝对定位的背景元素，确保完全覆盖
          let bgElement = document.getElementById('pwa-background')
          if (!bgElement) {
            bgElement = document.createElement('div')
            bgElement.id = 'pwa-background'
            const realHeight = window.innerHeight
            bgElement.style.cssText = `
              position: fixed !important;
              top: 0 !important;
              left: 0 !important;
              right: 0 !important;
              bottom: 0 !important;
              width: 100vw !important;
              height: ${realHeight}px !important;
              min-height: ${realHeight}px !important;
              z-index: -1000 !important;
              margin: 0 !important;
              padding: 0 !important;
              background-size: cover !important;
              background-position: center !important;
              background-repeat: no-repeat !important;
              background-attachment: scroll !important;
              pointer-events: none !important;
              -webkit-transform: translateZ(0) !important;
              transform: translateZ(0) !important;
            `
            document.body.appendChild(bgElement)
          }
          bgElement.style.backgroundImage = `url('${url}')`
          
          // 同时设置html和body为透明
          htmlElement.style.background = 'transparent'
          bodyElement.style.background = 'transparent'
        } else {
          // 移除背景元素
          const bgElement = document.getElementById('pwa-background')
          if (bgElement) {
            bgElement.remove()
          }
          htmlElement.style.background = 'transparent'
          bodyElement.style.background = 'transparent'
        }
      }
    }

    const loadImage = (url) => {
      const img = new Image()
      
      img.onload = () => {
        // iOS Safari: 延迟设置确保图片完全加载
        if (isIOS) {
          setTimeout(() => {
            isImageLoaded.value = true
            // iOS PWA模式下同时设置body背景
            setBodyBackground(url)
            // 立即强制重绘
            forceRepaint()
          }, 100)
        } else {
          isImageLoaded.value = true
          setBodyBackground(url)
        }
      }
      
      img.onerror = () => {
        isImageLoaded.value = false
        setBodyBackground(null)
      }
      
      img.src = url
    }
    
    // iOS PWA动态设置实际屏幕高度
    const setRealScreenHeight = () => {
      if (isIOS && window.matchMedia('(display-mode: standalone)').matches) {
        const realHeight = window.innerHeight
        if (backgroundRef.value) {
          backgroundRef.value.style.height = `${realHeight}px`
          backgroundRef.value.style.minHeight = `${realHeight}px`
        }
        
        // 同时设置pwa-background元素
        const bgElement = document.getElementById('pwa-background')
        if (bgElement) {
          bgElement.style.height = `${realHeight}px`
          bgElement.style.minHeight = `${realHeight}px`
        }
      }
    }
    
    onMounted(() => {
      loadImage(props.backgroundUrl)
      
      // iOS PWA模式下设置真实屏幕高度
      if (isIOS && window.matchMedia('(display-mode: standalone)').matches) {
        setRealScreenHeight()
        
        // 监听屏幕方向和尺寸变化
        window.addEventListener('resize', setRealScreenHeight)
        window.addEventListener('orientationchange', () => {
          setTimeout(setRealScreenHeight, 500)
        })
      }
    })
    
    watch(() => props.backgroundUrl, (newUrl, oldUrl) => {
      if (newUrl !== oldUrl) {
        // iOS Safari: 先重置状态再加载
        if (isIOS) {
          isImageLoaded.value = false
          // 强制清除当前显示
          nextTick(() => {
            loadImage(newUrl)
          })
        } else {
          loadImage(newUrl)
        }
      }
    })
    
    // 监听背景错误状态变化
    watch(() => props.isBackgroundError, (isError) => {
      if (isError) {
        setBodyBackground(null)
      } else {
        setBodyBackground(props.backgroundUrl)
      }
    })
    
    return {
      backgroundRef,
      backgroundStyle
    }
  }
}
</script>

<style scoped>
.background-layer {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  filter: blur(1px);
  transform: scale(1.05);
  transition: opacity 0.3s ease;
  opacity: 1;
  z-index: 1;
}

.background-layer.error {
  background: transparent;
  filter: none;
  transform: none;
}

.overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(2px);
  z-index: 2;
}

/* iOS PWA 模式背景图片适配 - 解决safe area裁剪问题 */
@supports (-webkit-touch-callout: none) {
  @media (display-mode: standalone) {
    .background-layer {
      /* 在iOS PWA模式下使用最低层级确保不被遮挡 */
      position: fixed !important;
      top: 0 !important;
      left: 0 !important;
      right: 0 !important;
      bottom: 0 !important;
      width: 100vw !important;
      height: 100vh !important;
      /* 移除不兼容的100dvh，使用window.innerHeight通过JS设置 */
      min-height: 100vh !important;
      margin: 0 !important;
      padding: 0 !important;
      z-index: -999 !important;
      /* 确保背景图片完全填充，特别针对iOS */
      background-size: cover !important;
      background-position: center !important;
      background-repeat: no-repeat !important;
      /* iOS上避免使用background-attachment: fixed */
      background-attachment: scroll !important;
      /* 确保图片优先级 */
      opacity: 1 !important;
      visibility: visible !important;
      /* 强制硬件加速 */
      -webkit-transform: translateZ(0) !important;
      transform: translateZ(0) !important;
      /* 确保完全覆盖safe area - 关键修复 */
      padding-top: 0 !important;
      padding-bottom: 0 !important;
      padding-left: 0 !important;
      padding-right: 0 !important;
      /* 强制延伸到safe area之外 */
      margin-top: calc(-1 * env(safe-area-inset-top)) !important;
      margin-bottom: calc(-1 * env(safe-area-inset-bottom)) !important;
      margin-left: calc(-1 * env(safe-area-inset-left)) !important;
      margin-right: calc(-1 * env(safe-area-inset-right)) !important;
      height: calc(100vh + env(safe-area-inset-top) + env(safe-area-inset-bottom)) !important;
      width: calc(100vw + env(safe-area-inset-left) + env(safe-area-inset-right)) !important;
    }
    
    .overlay {
      position: fixed !important;
      top: 0 !important;
      left: 0 !important;
      right: 0 !important;
      bottom: 0 !important;
      width: 100vw !important;
      height: 100vh !important;
      /* 移除不兼容的100dvh */
      min-height: 100vh !important;
      margin: 0 !important;
      padding: 0 !important;
      z-index: -998 !important;
      /* 强制硬件加速 */
      -webkit-transform: translateZ(0) !important;
      transform: translateZ(0) !important;
    }
  }
}

/* 深色模式支持 */
@media (prefers-color-scheme: dark) {
  .background-layer.error {
    background: transparent;
  }
}
</style>